/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: modules\pbxCli\PbxCommandSlotGrid.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import CommandSlotButton from './CommandSlotButton';
import type { ButtonConfig } from './ButtonConfigModal';

interface Slot {
    slotIndex: number;
    config: ButtonConfig | null;
}

interface PbxCommandSlotGridProps {
    slots: Slot[];
    isConnected: boolean;
    isLoading: boolean;
    onExecute: (command: string) => void;
    onEdit: (slotIndex: number) => void;
    t: (key: string, opts?: Record<string, unknown>) => string;
}

export function PbxCommandSlotGrid({ slots, isConnected, isLoading, onExecute, onEdit, t }: PbxCommandSlotGridProps) {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
                {t('common.loading')}
            </div>
        );
    }

    return (
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8">
            {slots.map((slot) => {
                const config = slot.config;
                const isConfigured = Boolean(config && config.enabled);

                const tooltip = isConfigured
                    ? config?.tooltip || config?.label || t('pbxCli.actions.editButton')
                    : t('pbxCli.actions.editButton');

                const disabled = Boolean(
                    isConfigured &&
                    !isConnected &&
                    config?.command &&
                    config.command.trim().toLowerCase() !== 'clear',
                );

                return (
                    <CommandSlotButton
                        key={slot.slotIndex}
                        slotIndex={slot.slotIndex}
                        isConfigured={isConfigured}
                        label={
                            isConfigured
                                ? config?.label || t('pbxCli.buttonConfigs.actions.edit')
                                : `${t('pbxCli.buttonConfigs.actions.add')} (${slot.slotIndex + 1})`
                        }
                        tooltip={tooltip}
                        command={config?.command}
                        disabled={disabled}
                        editLabel={t('pbxCli.actions.editButton')}
                        onExecute={onExecute}
                        onEdit={onEdit}
                    />
                );
            })}
        </div>
    );
}
